import { useState, useCallback } from "react";
import { Copy, CheckCircle, ChevronDown, ChevronUp, Server, Palette, FileCode, Database, Globe, Layout, Mic, Package, Download } from "lucide-react";

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 bg-white/5 hover:bg-white/10 transition-colors text-left">
        <Icon className="w-5 h-5 text-[#4285F4]" />
        <span className="text-white font-bold text-lg flex-1">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 py-4 text-gray-300 text-sm leading-relaxed">{children}</div>}
    </div>
  );
};

const CodeBlock = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative my-3">
      {label && <span className="text-xs text-gray-500 mb-1 block">{label}</span>}
      <pre className="bg-black/50 border border-white/10 rounded-lg p-4 overflow-x-auto text-xs text-green-400 font-mono whitespace-pre-wrap">{code}</pre>
      <button onClick={copy} className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 transition-colors">
        {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
      </button>
    </div>
  );
};

/* ─── Logo Downloader — Genera PNG con Canvas ─── */
const LogoDownloader = () => {
  const [downloading, setDownloading] = useState(false);

  const drawWide = useCallback((canvas, scale = 1) => {
    const ctx = canvas.getContext("2d");
    const W = 1600 * scale;
    const H = 600 * scale;
    canvas.width = W;
    canvas.height = H;
    ctx.fillStyle = "#0a0e17";
    ctx.fillRect(0, 0, W, H);
    const mainSize = 96 * scale;
    ctx.font = `900 ${mainSize}px Poppins, sans-serif`;
    ctx.fillStyle = "#E8B61B";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("schedabusiness.it", W / 2, H * 0.42);
    const payoffSize = 28 * scale;
    ctx.font = `italic 500 ${payoffSize}px Poppins, sans-serif`;
    ctx.fillStyle = "#E6E1D2";
    ctx.globalAlpha = 0.7;
    ctx.fillText("se non ti trovano, non esisti", W / 2, H * 0.65);
    ctx.globalAlpha = 1;
  }, []);

  const drawSquare = useCallback((canvas, scale = 1) => {
    const ctx = canvas.getContext("2d");
    const S = 1080 * scale;
    canvas.width = S;
    canvas.height = S;
    ctx.fillStyle = "#0a0e17";
    ctx.fillRect(0, 0, S, S);
    // Main text — split on 2 lines for square
    const mainSize = 72 * scale;
    ctx.font = `900 ${mainSize}px Poppins, sans-serif`;
    ctx.fillStyle = "#E8B61B";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("scheda", S / 2, S * 0.36);
    ctx.fillText("business.it", S / 2, S * 0.50);
    // Payoff
    const payoffSize = 22 * scale;
    ctx.font = `italic 500 ${payoffSize}px Poppins, sans-serif`;
    ctx.fillStyle = "#E6E1D2";
    ctx.globalAlpha = 0.7;
    ctx.fillText("se non ti trovano, non esisti", S / 2, S * 0.65);
    ctx.globalAlpha = 1;
  }, []);

  const handleDownload = useCallback(async (variant, format) => {
    setDownloading(true);
    await document.fonts.ready;
    const offscreen = document.createElement("canvas");
    const drawFn = variant === "square" ? drawSquare : drawWide;
    drawFn(offscreen, 3);
    const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
    const quality = format === "jpg" ? 0.95 : undefined;
    const dataUrl = offscreen.toDataURL(mimeType, quality);
    const link = document.createElement("a");
    link.download = `logo-schedabusiness-${variant}.${format}`;
    link.href = dataUrl;
    link.click();
    setDownloading(false);
  }, [drawWide, drawSquare]);

  return (
    <div className="space-y-5 mb-4">
      {/* ── LOGO ORIZZONTALE ── */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-bold tracking-widest text-gray-500 uppercase">Orizzontale</span>
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#E8B61B]/20 text-[#E8B61B] font-bold">16:6</span>
        </div>
        <div className="bg-[#0a0e17] rounded-xl p-8 mb-4 flex flex-col items-center border border-white/5">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#E8B61B] mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>
            schedabusiness.it
          </h2>
          <p className="text-[#E6E1D2]/70 italic text-base sm:text-lg" style={{ textShadow: "0 0 20px rgba(255,255,255,0.15)", fontFamily: "Poppins, sans-serif" }}>
            se non ti trovano, non esisti
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => handleDownload("wide", "png")} disabled={downloading} className="flex items-center gap-2 px-4 py-2.5 bg-[#E8B61B] text-black font-bold rounded-xl hover:bg-[#d4a518] transition-all text-sm" data-testid="download-logo-wide-png">
            <Download className="w-4 h-4" /> PNG
          </button>
          <button onClick={() => handleDownload("wide", "jpg")} disabled={downloading} className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-all text-sm border border-white/10" data-testid="download-logo-wide-jpg">
            <Download className="w-4 h-4" /> JPG
          </button>
        </div>
        <p className="text-gray-500 text-xs mt-2">4800x1800px — social, header, stampa.</p>
      </div>

      {/* ── LOGO QUADRATO (per Scheda Google) ── */}
      <div className="bg-white/5 border border-[#E8B61B]/30 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-bold tracking-widest text-[#E8B61B] uppercase">Quadrato — Logo Scheda Google</span>
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#E8B61B]/20 text-[#E8B61B] font-bold">1:1</span>
        </div>
        <div className="bg-[#0a0e17] rounded-xl mb-4 flex flex-col items-center justify-center border border-white/5 aspect-square max-w-xs mx-auto p-6">
          <div className="text-center" style={{ fontFamily: "Poppins, sans-serif" }}>
            <div className="text-3xl sm:text-4xl font-black text-[#E8B61B] leading-tight">scheda</div>
            <div className="text-3xl sm:text-4xl font-black text-[#E8B61B] leading-tight">business.it</div>
          </div>
          <p className="text-[#E6E1D2]/70 italic text-sm mt-3" style={{ textShadow: "0 0 20px rgba(255,255,255,0.15)", fontFamily: "Poppins, sans-serif" }}>
            se non ti trovano, non esisti
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => handleDownload("square", "png")} disabled={downloading} className="flex items-center gap-2 px-5 py-3 bg-[#E8B61B] text-black font-bold rounded-xl hover:bg-[#d4a518] transition-all text-sm" data-testid="download-logo-square-png">
            <Download className="w-4 h-4" /> Scarica PNG Quadrato
          </button>
          <button onClick={() => handleDownload("square", "jpg")} disabled={downloading} className="flex items-center gap-2 px-5 py-3 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-all text-sm border border-white/10" data-testid="download-logo-square-jpg">
            <Download className="w-4 h-4" /> JPG Quadrato
          </button>
        </div>
        <p className="text-gray-500 text-xs mt-2">3240x3240px — perfetto per logo Scheda Google Business (1:1).</p>
      </div>
    </div>
  );
};

export default function AdminPanel() {
  return (
    <div className="min-h-screen bg-[#080A0E] text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-2 text-[#EA4335] text-xs font-bold tracking-widest mb-2">RISERVATO</div>
          <h1 className="text-3xl font-black text-white mb-2">Documentazione Tecnica</h1>
          <p className="text-gray-400 text-base">Tutte le informazioni per il tuo programmatore e grafico. Questa pagina non e' indicizzata dai motori di ricerca.</p>
          <p className="text-gray-500 text-sm mt-2">schedabusiness.it — Progetto TANDEM (.it / .com)</p>
        </div>

        {/* Tech Stack */}
        <Section title="Stack Tecnologico" icon={Server} defaultOpen={true}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-white font-bold mb-2">Frontend</h4>
              <ul className="space-y-1 text-sm">
                <li>React 18 (CRA + Craco)</li>
                <li>Tailwind CSS 3</li>
                <li>Framer Motion (animazioni)</li>
                <li>Lucide React (icone)</li>
                <li>Shadcn/UI (componenti base)</li>
                <li>React Router DOM v6</li>
                <li>Pannellum (viewer 360)</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-2">Backend</h4>
              <ul className="space-y-1 text-sm">
                <li>Python 3.11 + FastAPI</li>
                <li>MongoDB (Motor async driver)</li>
                <li>Uvicorn (ASGI server)</li>
                <li>CORS abilitato</li>
              </ul>
            </div>
          </div>
          <CodeBlock label="Installazione Frontend" code={`cd frontend
yarn install
yarn start        # dev (porta 3000)
yarn build        # produzione`} />
          <CodeBlock label="Installazione Backend" code={`cd backend
pip install -r requirements.txt
uvicorn server:app --host 0.0.0.0 --port 8001 --reload`} />
          <CodeBlock label="Variabili d'ambiente — frontend/.env" code={`REACT_APP_BACKEND_URL=https://tuodominio.com
WDS_SOCKET_PORT=443`} />
          <CodeBlock label="Variabili d'ambiente — backend/.env" code={`MONGO_URL=mongodb://localhost:27017
DB_NAME=test_database
CORS_ORIGINS=*`} />
        </Section>

        {/* File Structure */}
        <Section title="Struttura File" icon={FileCode}>
          <CodeBlock code={`/app/
├── backend/
│   ├── server.py              # API FastAPI (tutti gli endpoint)
│   ├── requirements.txt       # Dipendenze Python
│   ├── .env                   # Config backend
│   └── voice_notes/           # Note vocali uploadate
├── frontend/
│   ├── public/
│   │   ├── index.html         # SEO TANDEM (meta, hreflang, JSON-LD)
│   │   ├── sitemap.xml        # Sitemap per Google
│   │   ├── robots.txt         # Regole crawler
│   │   └── manifest.json      # PWA manifest
│   └── src/
│       ├── App.js             # Router principale
│       ├── index.css           # CSS globale + variabili + animazioni
│       ├── components/
│       │   ├── VoiceNoteRecorder.js  # Microfono REC (FAB + pannello)
│       │   ├── InternalNav.js        # Navigazione pagine interne
│       │   └── ui/                   # Componenti Shadcn
│       └── pages/
│           ├── LandingPage.jsx       # PAGINA PRINCIPALE (~2400 righe)
│           ├── TourViewer.jsx        # Viewer Pannellum 360
│           ├── EmailTemplate.jsx     # Template email agenzie
│           ├── AgenciesToscana.jsx   # Database agenzie + email
│           ├── StrategiaEnti.jsx     # Strategia enti pubblici
│           └── Roadmap.jsx           # Piano commerciale completo
└── 360_images/                # Immagini panoramiche 360`} />
        </Section>

        {/* API Endpoints */}
        <Section title="API Endpoints" icon={Globe}>
          <div className="space-y-3">
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">GET /api/</code>
              <p className="text-gray-400 text-xs mt-1">Health check — ritorna versione API</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">GET /api/tours</code>
              <p className="text-gray-400 text-xs mt-1">Lista tutti i tour con stanze e hotspot</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">GET /api/tours/:id</code>
              <p className="text-gray-400 text-xs mt-1">Dettaglio singolo tour</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">POST /api/contact</code>
              <p className="text-gray-400 text-xs mt-1">Invio form contatto — salva su MongoDB</p>
              <CodeBlock code={`{ "name": "...", "email": "...", "phone": "...", "message": "...", "package": "start|plus|rooftop" }`} />
            </div>
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">POST /api/voice-note</code>
              <p className="text-gray-400 text-xs mt-1">Upload nota vocale (multipart/form-data)</p>
              <CodeBlock code={`FormData: audio (file .webm) + cases (string con problemi selezionati)`} />
            </div>
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">GET /api/stats</code>
              <p className="text-gray-400 text-xs mt-1">Statistiche (tour totali, stanze, contatti)</p>
            </div>
            <div className="bg-black/30 rounded-lg p-3 border border-white/5">
              <code className="text-green-400 text-xs">GET /api/voice-notes/:filename</code>
              <p className="text-gray-400 text-xs mt-1">Static file serving note vocali</p>
            </div>
          </div>
        </Section>

        {/* Design System */}
        <Section title="Design System — Palette e Font" icon={Palette}>
          {/* LOGO UFFICIALE ORO — Download PNG */}
          <h4 className="text-white font-bold mb-3">Logo Ufficiale</h4>
          <p className="text-gray-400 text-sm mb-4">Il logo ufficiale e' testo "schedabusiness.it" in font Poppins Black (900), colore Oro Solido (#E8B61B), con payoff.</p>
          
          <LogoDownloader />

          <h4 className="text-white font-bold mb-3 mt-6">Colori Principali</h4>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { name: "Background", hex: "#0c1220", desc: "Dark mode base" },
              { name: "Background Alt", hex: "#080A0E", desc: "Pagine interne" },
              { name: "Blu Google", hex: "#4285F4", desc: "CTA, link, nav attivo" },
              { name: "Rosso Google", hex: "#EA4335", desc: "Urgenza, REC, header mic" },
              { name: "Giallo Google", hex: "#FBBC04", desc: "Highlight, badge" },
              { name: "Verde Google", hex: "#34A853", desc: "Successo, invio, conferma" },
            ].map((c, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-2">
                <div className="w-8 h-8 rounded-md flex-shrink-0 border border-white/10" style={{ backgroundColor: c.hex }} />
                <div>
                  <div className="text-white text-xs font-bold">{c.name}</div>
                  <div className="text-gray-500 text-[10px] font-mono">{c.hex}</div>
                  <div className="text-gray-400 text-[10px]">{c.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <h4 className="text-white font-bold mb-3">Tipografia</h4>
          <p className="text-gray-400 text-sm mb-2">Target: utenti 65+ anni (Boomer-friendly). Font grandi, mai sotto text-sm.</p>
          <CodeBlock code={`Body: text-base (16px) minimo
H1: text-4xl sm:text-5xl lg:text-6xl (grassetto/black)
H2: text-xl sm:text-2xl
Sottotitoli: text-lg
Minimo storico: text-sm (14px) — solo per note secondarie
Font: system-ui (default Tailwind)`} />

          <h4 className="text-white font-bold mt-4 mb-3">Stile UI</h4>
          <ul className="space-y-1 text-sm">
            <li>Full Dark Mode — NO sfondi bianchi sulla landing</li>
            <li>Glassmorphism (backdrop-blur-xl + bg-white/5)</li>
            <li>Border sottili (border-white/10)</li>
            <li>Ombre: shadow-2xl su card principali</li>
            <li>Rounded: rounded-2xl su card, rounded-full su bottoni CTA</li>
            <li>Animazioni: Framer Motion per entrata, hover, esplosione pacchetti</li>
          </ul>
        </Section>

        {/* Copywriting */}
        <Section title="Regole Copywriting PNL" icon={Layout}>
          <ul className="space-y-2 text-sm">
            <li><strong className="text-[#EA4335]">Sempre il TU:</strong> Mai dare del "Lei". Rivolgersi al cliente con il "tu", tono diretto e confidenziale.</li>
            <li><strong className="text-[#FBBC04]">Tono da Closer:</strong> Aggressivo ma ruffiano. Stile PNL/NLP con pattern interrupts.</li>
            <li><strong className="text-[#4285F4]">Urgenza e Scarsita:</strong> "Solo 12 posti", "47 richieste in coda", "preordine obbligatorio".</li>
            <li><strong className="text-[#34A853]">Deducibilita 100%:</strong> Enfatizzare sempre che il virtual tour e' spesa pubblicitaria deducibile.</li>
            <li><strong className="text-white">Target:</strong> Agenzie immobiliari, enti pubblici, scuole. Eta' media 50-65+.</li>
          </ul>
        </Section>

        {/* Pricing */}
        <Section title="Pacchetti e Prezzi Attuali" icon={Package}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="py-2 text-gray-400">Pacchetto</th>
                  <th className="py-2 text-gray-400">Prezzo</th>
                  <th className="py-2 text-gray-400">Formazione</th>
                  <th className="py-2 text-gray-400">Include</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/5">
                  <td className="py-3 text-[#4285F4] font-bold">START</td>
                  <td className="py-3 text-white font-bold">€99</td>
                  <td className="py-3">+€199</td>
                  <td className="py-3 text-gray-400">Virtual Tour + Google Maps</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 text-[#FBBC04] font-bold">PLUS</td>
                  <td className="py-3 text-white font-bold">€299</td>
                  <td className="py-3">+€149</td>
                  <td className="py-3 text-gray-400">Virtual Tour + Maps + Timewarp</td>
                </tr>
                <tr>
                  <td className="py-3 text-[#EA4335] font-bold">ROOFTOP</td>
                  <td className="py-3 text-white font-bold">€599</td>
                  <td className="py-3 text-[#34A853] font-bold">IN REGALO</td>
                  <td className="py-3 text-gray-400">Tutto incluso + Formazione gratis</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-500 text-xs mt-3">Pagamento in 3 rate PayPal (TAEG 0%). Deducibile 100% come spesa pubblicitaria (art. 108 TUIR).</p>
        </Section>

        {/* Voice Recorder */}
        <Section title="VoiceNoteRecorder (Microfono REC)" icon={Mic}>
          <p className="mb-3">Componente floating per raccogliere note vocali dai clienti con problemi su Google Business.</p>
          <ul className="space-y-1 text-sm mb-3">
            <li>FAB button rosso fisso in basso a sinistra (z-index 50)</li>
            <li>Pannello con 10 checkbox problemi predefiniti</li>
            <li>Registrazione audio WebM/Opus a 16kbps (file leggerissimi)</li>
            <li>Richiede HTTPS e permessi microfono del browser</li>
            <li>Invio a POST /api/voice-note (salva in /backend/voice_notes/)</li>
          </ul>
          <CodeBlock label="10 Problemi predefiniti" code={`Profilo sospeso | Recensione fasulla | Cambio posizione | Numero o GPS errato
Foto rifiutate | Scheda duplicata | Verifiche bloccate | Orari sbagliati
Scheda rimossa | Virtual Tour mancante`} />
        </Section>

        {/* SEO TANDEM */}
        <Section title="SEO TANDEM (.it / .com)" icon={Globe}>
          <p className="mb-3">Il progetto TANDEM prevede il crossfade perfetto tra schedabusiness.it e schedabusiness.com.</p>
          <CodeBlock label="Hreflang (in index.html)" code={`<link rel="alternate" hreflang="it" href="https://schedabusiness.it" />
<link rel="alternate" hreflang="en" href="https://schedabusiness.com" />
<link rel="alternate" hreflang="x-default" href="https://schedabusiness.com" />`} />
          <CodeBlock label="Meta SEO chiave" code={`Title: schedabusiness.it — Virtual Tour 360° per Agenzie, Enti, Scuole
Description: Pacchetti Virtual Tour 360° per Google Street View...
Keywords: virtual tour 360, google street view, agenzie immobiliari...
OG/Twitter: Configurati per condivisione social`} />
          <p className="text-gray-400 text-sm">File SEO: sitemap.xml, robots.txt, manifest.json tutti in /public/</p>
        </Section>

        {/* Deployment */}
        <Section title="Deploy Autonomo (Senza Emergent)" icon={Server}>
          <p className="mb-3 text-[#EA4335] font-bold">Istruzioni per deploy su hosting proprio:</p>
          <CodeBlock label="1. Clona il repo da GitHub" code={`git clone https://github.com/TUO-USER/TUO-REPO.git
cd TUO-REPO`} />
          <CodeBlock label="2. Backend (qualsiasi VPS: DigitalOcean, Hetzner, AWS)" code={`# Installa MongoDB
sudo apt install mongodb

# Setup backend
cd backend
pip install -r requirements.txt

# Crea .env
echo 'MONGO_URL=mongodb://localhost:27017' > .env
echo 'DB_NAME=schedabusiness' >> .env
echo 'CORS_ORIGINS=https://schedabusiness.it' >> .env

# Avvia con systemd o pm2
uvicorn server:app --host 0.0.0.0 --port 8001`} />
          <CodeBlock label="3. Frontend (Vercel, Netlify, o stesso VPS)" code={`cd frontend

# Crea .env
echo 'REACT_APP_BACKEND_URL=https://api.schedabusiness.it' > .env

# Build
yarn install
yarn build

# La cartella build/ contiene il sito statico
# Caricala su Vercel/Netlify o servila con Nginx`} />
          <CodeBlock label="4. Nginx config (se stesso VPS)" code={`server {
    listen 80;
    server_name schedabusiness.it www.schedabusiness.it;

    # Frontend
    location / {
        root /var/www/schedabusiness/frontend/build;
        try_files $uri /index.html;
    }

    # Backend API proxy
    location /api/ {
        proxy_pass http://127.0.0.1:8001;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
    }
}`} />
          <CodeBlock label="5. SSL con Let's Encrypt" code={`sudo apt install certbot python3-certbot-nginx
sudo certbot --nginx -d schedabusiness.it -d www.schedabusiness.it`} />
          <p className="text-gray-400 text-sm mt-3">Costo stimato hosting autonomo: ~€5-10/mese (DigitalOcean droplet o Hetzner CX22)</p>
        </Section>

        {/* Routes */}
        <Section title="Pagine e Routing" icon={Layout}>
          <div className="space-y-2">
            {[
              { path: "/", desc: "Landing Page principale (dark mode, hero, pacchetti, VAK test, ecc.)", visible: true },
              { path: "/tour/:tourId", desc: "Viewer 360 Pannellum", visible: true },
              { path: "/agenzie", desc: "Database agenzie immobiliari Toscana + email template", visible: false },
              { path: "/email", desc: "Template email copia-incolla per agenzie", visible: false },
              { path: "/enti", desc: "Strategia per enti pubblici e fondazioni", visible: false },
              { path: "/roadmap", desc: "Piano commerciale completo (agenzie + enti + scuole)", visible: false },
              { path: "/admin", desc: "Questa pagina — documentazione tecnica", visible: false },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2">
                <code className="text-[#4285F4] text-xs font-mono min-w-[120px]">{r.path}</code>
                <span className="text-gray-400 text-xs flex-1">{r.desc}</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full ${r.visible ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {r.visible ? "Pubblica" : "Nascosta"}
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-white/10 text-center">
          <p className="text-gray-500 text-xs">schedabusiness.it — Documentazione tecnica riservata</p>
          <p className="text-gray-600 text-[10px] mt-1">Riccardo Rossi — Fotografo Google Street View Certificato dal 2016</p>
        </div>
      </div>
    </div>
  );
}
